import { Table } from '@mantine/core'
import { FormattedMessage } from 'react-intl'
import { DataTable } from '@/components/ui-shared/Tables/DataTable'
import { SortableLabel } from '@/components/ui-shared/Tables/SortableLabel/SortableLabel'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import {
  useGetEdgeClientVersion,
  useInvalidateDeviceList
} from '@/queries/deviceQueries'
import { Device } from '@/types/device'
import { CrudAction, Resource } from '@/types/permissions'
import { SortConfig } from '@/utils/sorting'
import { DeviceListItem } from './DeviceListItem'

export type TableSortConfig = SortConfig<'label'>

type DeviceListProps = {
  devices: Device[]
  sortConfig: TableSortConfig
  isLoading?: boolean
  hasError?: boolean
  onSort: (sortConfig: TableSortConfig) => void
  onReload?: () => void
}

export const DeviceList = ({
  devices,
  sortConfig,
  isLoading,
  hasError,
  onSort,
  onReload
}: DeviceListProps) => {
  const { invalidateDeviceList } = useInvalidateDeviceList()

  const { data: edgeClientVersionData } = useGetEdgeClientVersion()

  const [canManageDevices] = useHasPermissions(Resource.Devices, [
    CrudAction.Create
  ])

  return (
    <DataTable
      hasItems={devices.length > 0}
      isLoading={isLoading}
      hasError={hasError}
      emptyMessage={<FormattedMessage id="devices.list.empty" />}
      onReload={onReload}
    >
      <Table.Thead>
        <Table.Tr>
          <Table.Th w={32}>&nbsp;</Table.Th>

          <Table.Th>
            <SortableLabel
              label={<FormattedMessage id="devices.fields.name" />}
              sortKey="label"
              sortConfig={sortConfig}
              disabled={devices.length === 0}
              onSort={onSort}
            />
          </Table.Th>

          <Table.Th>
            <FormattedMessage id="devices.fields.deviceType" />
          </Table.Th>

          <Table.Th>
            <FormattedMessage id="devices.fields.status" />
          </Table.Th>

          <Table.Th>
            <FormattedMessage id="devices.fields.gpus" />
          </Table.Th>

          <Table.Th>
            <FormattedMessage id="devices.fields.edgeClient" />
          </Table.Th>

          <Table.Th>
            <FormattedMessage id="devices.fields.deepStream" />
          </Table.Th>

          <Table.Th>&nbsp;</Table.Th>
        </Table.Tr>
      </Table.Thead>

      <Table.Tbody>
        {devices.map((device) => (
          <DeviceListItem
            key={device.id}
            device={device}
            showActions={canManageDevices}
            latestEdgeClientVersion={edgeClientVersionData?.trim()}
            onActionSettled={() => void invalidateDeviceList()}
          />
        ))}
      </Table.Tbody>
    </DataTable>
  )
}
